import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MapPin, ArrowUpRight, Instagram, Phone } from "lucide-react";
import { BRAND } from "../lib/constants";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/menu", label: "Menu" },
  { to: "/locations", label: "Locations" },
  { to: "/delivery", label: "Delivery" },
  { to: "/areas-served", label: "Areas" },
  { to: "/education", label: "Education" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
];

// Rotating topbar announcements — cycle every 4.5s to keep the header fresh.
// `to` uses react-router for internal navigation; `href` for external.
const ANNOUNCEMENTS = [
  {
    key: "free-joint",
    full: "✦ First Joint's On Us · Free pre-roll for new customers ✦",
    short: "✦ First joint's on us ✦",
    to: "/menu",
  },
  {
    key: "daily-deal",
    full: "✦ Daily Deals · Fresh drops from Alt Sol & top DC cultivators ✦",
    short: "✦ Daily deals live ✦",
    to: "/menu",
  },
  {
    key: "delivery",
    full: "✦ Same-Day DC Delivery · Fast, discreet, licensed ✦",
    short: "✦ Same-day DC delivery ✦",
    to: "/delivery",
  },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [announceIndex, setAnnounceIndex] = useState(0);
  const [announceVisible, setAnnounceVisible] = useState(true);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  // Rotate announcements with a fade transition every ~4.5s
  useEffect(() => {
    const rotateMs = 4500;
    const fadeMs = 350;
    const id = setInterval(() => {
      setAnnounceVisible(false);
      setTimeout(() => {
        setAnnounceIndex((i) => (i + 1) % ANNOUNCEMENTS.length);
        setAnnounceVisible(true);
      }, fadeMs);
    }, rotateMs);
    return () => clearInterval(id);
  }, []);

  const current = ANNOUNCEMENTS[announceIndex];

  return (
    <>
      {/* Top Announcement Bar — clean 3-column: status · featured message · minimal contact */}
      <div className="bg-ink text-white text-[11px] uppercase tracking-[0.25em]" data-testid="topbar">
        <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between gap-4">
          {/* Left — status pill (visible on all screens) */}
          <div className="flex items-center gap-2 text-white/70 shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="hidden sm:inline">Open Daily · Until Late</span>
            <span className="sm:hidden">Open</span>
          </div>

          {/* Center — rotating announcement (free-joint · daily deals · delivery) */}
          <Link
            key={current.key}
            to={current.to}
            className={`text-brand hover:text-white text-center truncate transition-opacity duration-300 ${
              announceVisible ? "opacity-100" : "opacity-0"
            }`}
            data-testid="topbar-announcement"
            aria-live="polite"
          >
            <span className="hidden sm:inline">{current.full}</span>
            <span className="sm:hidden">{current.short}</span>
          </Link>

          {/* Right — minimal contact (phone + IG only) */}
          <div className="flex items-center gap-4 sm:gap-5 shrink-0 text-white/60">
            <a
              href={BRAND.phoneHref}
              className="hover:text-white transition-colors hidden sm:inline"
              data-testid="topbar-phone"
            >
              {BRAND.phone}
            </a>
            <a
              href={BRAND.phoneHref}
              className="hover:text-white transition-colors sm:hidden"
              data-testid="topbar-phone-mobile"
              aria-label="Call District Wellness Center"
            >
              <Phone size={12} />
            </a>
            <a
              href={BRAND.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-brand transition-colors"
              aria-label="District Wellness Center on Instagram"
              data-testid="topbar-instagram"
            >
              <Instagram size={12} />
            </a>
          </div>
        </div>
      </div>

      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white/85 backdrop-blur-xl border-b border-black/5" : "bg-white/60 backdrop-blur-md"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-24 sm:h-28">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img
              src={BRAND.logoFull}
              alt="District Wellness Center — Medical Cannabis Dispensary in Washington DC"
              className="h-14 sm:h-16 lg:h-20 w-auto object-contain"
            />
            <div className="hidden md:block leading-tight border-l border-ink/10 pl-3">
              <div className="text-[10px] uppercase tracking-[0.3em] text-ink-muted">Licensed DC</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-ink-muted mt-0.5">Est. Georgia Ave</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-[13px] uppercase tracking-[0.18em] link-underline transition-colors ${
                    isActive ? "text-brand" : "text-ink hover:text-brand"
                  }`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/menu"
              data-testid="header-order-online-btn"
              className="hidden sm:inline-flex items-center gap-2 bg-ink text-white px-5 py-3 text-[11px] uppercase tracking-[0.2em] hover:bg-brand transition-colors"
            >
              Order Online <ArrowUpRight size={14} />
            </Link>
            <button
              className="lg:hidden p-2 -mr-2"
              aria-label="Open menu"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-black/5 bg-white" data-testid="mobile-menu">
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  className="text-sm uppercase tracking-[0.2em] text-ink hover:text-brand"
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  end={n.to === "/"}
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/menu"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-ink text-white px-5 py-3 text-[11px] uppercase tracking-[0.2em]"
                data-testid="mobile-order-online-btn"
              >
                Order Online <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
